import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { monthKeys, timeKeys } from "@/lib/query";
import { ApiError, sendJson } from "@/lib/time/api-client";
import type {
  CreateEntryInput,
  DeleteEntryInput,
  EntryWriteResult,
  UpdateEntryInput,
} from "@/schemas/time";

/** The server's answer to a create whose first attempt had already saved (a retry after a 504). */
export const ALREADY_SAVED = "Already saved";

/**
 * Every loaded week view (any week) and Entries Month view, so grids and Entries re-read after a
 * write: the month lists the same entries with the same lock flags.
 */
export function invalidateWeeks(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: timeKeys.weeks() }),
    queryClient.invalidateQueries({ queryKey: timeKeys.monthEntriesAll() }),
  ]);
}

/** Every loaded assignable search (favorites change what the pickers list). */
export function invalidateAssignable(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: timeKeys.assignableAll() });
}

/** Every loaded month view (Home, Guidance): logged hours and totals change with a time write. */
export function invalidateMonths(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: monthKeys.all });
}

/** Weeks, sign-offs and months: an entry write changes a week's grid, its missing hours and Home's totals. */
export function invalidateAfterEntryWrite(queryClient: QueryClient) {
  return Promise.all([
    invalidateWeeks(queryClient),
    invalidateMonths(queryClient),
    queryClient.invalidateQueries({ queryKey: timeKeys.signOffs() }),
  ]);
}

/** The server's answer when a write may or may not have landed ("This may have saved"). */
const MAY_HAVE_SAVED = 504;

/** A may-have-saved write can have landed, so it refreshes like a save; other errors changed nothing. */
export async function invalidateAfterEntryError(queryClient: QueryClient, err: Error) {
  if (err instanceof ApiError && err.status === MAY_HAVE_SAVED) {
    await invalidateAfterEntryWrite(queryClient);
  }
}

/** Entry ids are made lower-case in the browser; the warehouse hands them back upper-case. */
export function sameId(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * "Already saved" means the entry exists, so a create answered with it is a success, not a
 * warning to show. Everything else the server says stays a warning.
 */
export function splitWarnings(result: EntryWriteResult): {
  alreadySaved: boolean;
  warnings: string[];
} {
  return {
    alreadySaved: result.warnings.includes(ALREADY_SAVED),
    warnings: result.warnings.filter((w) => w !== ALREADY_SAVED),
  };
}

/** The server's answer when the save rules refuse a write; its message names the rule. */
export const RULE_REFUSAL = 409;

export interface EntryMutationOptions {
  /**
   * The caller shows rule refusals (409) itself, next to the value (the grid's row chip), so
   * the hook does not toast them too. Every other error still toasts.
   */
  quietRefusals?: boolean;
}

export function shouldToastError(err: Error, options: EntryMutationOptions = {}): boolean {
  return !(options.quietRefusals && err instanceof ApiError && err.status === RULE_REFUSAL);
}

function useEntryMutation<I>(
  send: (input: I) => Promise<EntryWriteResult>,
  options: EntryMutationOptions,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: async (result) => {
      toast.success("Saved", { id: "time-saved" });
      for (const warning of splitWarnings(result).warnings) toast.warning(warning);
      await invalidateAfterEntryWrite(queryClient);
    },
    // A 504 "This may have saved" lands here and rejects to the caller; writes are never retried.
    onError: async (err) => {
      if (shouldToastError(err, options)) toast.error(err.message);
      await invalidateAfterEntryError(queryClient, err);
    },
    retry: false,
  });
}

export function useCreateEntry(options: EntryMutationOptions = {}) {
  return useEntryMutation(
    (input: CreateEntryInput) => sendJson<EntryWriteResult>("POST", "/api/time/entries", input),
    options,
  );
}

export function useUpdateEntry(options: EntryMutationOptions = {}) {
  return useEntryMutation(
    (input: UpdateEntryInput) => sendJson<EntryWriteResult>("PUT", "/api/time/entries", input),
    options,
  );
}

export function useDeleteEntry(options: EntryMutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteEntryInput) =>
      sendJson<{ id: string }>("POST", "/api/time/entries/delete", input),
    onSuccess: async () => {
      toast.success("Deleted", { id: "time-saved" });
      await invalidateAfterEntryWrite(queryClient);
    },
    onError: async (err) => {
      if (shouldToastError(err, options)) toast.error(err.message);
      await invalidateAfterEntryError(queryClient, err);
    },
    retry: false,
  });
}
